.resources-page {
  // 与 Home / Products / AboutUs 保持一致的容器与断点策略
  --content-max: 1500px;
  --container-pad: clamp(16px, 4vw, 48px);
  --tap-min: 44px;
  --section-gap: clamp(32px, 6vw, 120px);

  .page {
    width: 100%;
    min-height: 100%;
    height: auto;
    overflow: visible;
    position: relative;
    background-color: rgba(255, 255, 255, 1);
  }

  // ---------------------------------------------------------------------------
  // Hero
  // ---------------------------------------------------------------------------
  .resources-hero {
    position: relative;
    width: 100%;
    min-height: clamp(260px, 34vh, 480px);
    background: url(../../assets/images/pages-resources-resources-hero.png) no-repeat;
    background-size: cover;
    background-position: center;
    overflow: hidden;
  }

  .resources-hero__overlay {
    position: absolute;
    inset: 0;
    background-color: rgba(22, 0, 4, 0.5);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
  }

  // ---------------------------------------------------------------------------
  // Main
  // ---------------------------------------------------------------------------
  .resources-main {
    width: 100%;
  }

  .resources-intro {
    width: 100%;
    max-width: var(--content-max);
    margin: var(--section-gap) auto 0;
    padding-inline: var(--container-pad);
    box-sizing: border-box;
    text-align: center;
  }

  .resources-intro__title {
    margin: 0;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(40px, 6vw, 80px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    text-align: center;
    text-transform: uppercase;
    line-height: 1;
    white-space: normal;
  }

  .resources-intro__lead {
    margin: clamp(18px, 3.5vw, 40px) auto 0;
    max-width: min(92ch, 100%);
    color: rgba(124, 124, 124, 1);
    font-size: clamp(16px, 1.6vw, 24px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    text-align: center;
    line-height: 1.6;
  }

  // ---------------------------------------------------------------------------
  // Resource cards
  // ---------------------------------------------------------------------------
  .resource-cards {
    width: 100%;
    max-width: calc(var(--content-max) + 320px);
    margin: clamp(40px, 6vw, 120px) auto 0;
    padding-inline: var(--container-pad);
    box-sizing: border-box;
    // 用 flex-wrap + justify-content 实现“换行也整体居中”
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-items: stretch;
    --cards-gap: clamp(16px, 2vw, 32px);
    gap: var(--cards-gap);

    > * {
      min-width: 0;
    }
  }

  .resource-card {
    background-color: rgba(239, 236, 229, 1);
    border-radius: 0px 24px 24px 0px;
    padding: clamp(40px, 5vw, 120px) clamp(28px, 3.5vw, 56px);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    min-height: clamp(380px, 45vw, 810px);
    // 桌面：三列；最后一行换行也会整体居中
    flex: 0 1 calc((100% - var(--cards-gap) * 2) / 3);
    max-width: 580px;
  }

  .resource-card__title {
    margin: 0;
    color: rgba(163, 135, 95, 1);
    font-size: clamp(24px, 2.2vw, 36px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    text-align: center;
    line-height: 1.1;
    white-space: normal;
    text-transform: none;
  }

  .resource-card__iconWrap {
    margin: clamp(28px, 4.2vw, 75px) 0px clamp(25px, 4vw, 70px);
    width: clamp(180px, 18vw, 300px);
    height: clamp(180px, 18vw, 300px);
    border-radius: 999px;
    background-color: rgba(255, 255, 255, 1);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .resource-card__icon {
    width: clamp(76px, 7vw, 128px);
    height: auto;
    max-height: 60%;
    object-fit: contain;
    display: block;
  }

  .resource-card__cta {
    margin-top: auto;
    width: min(100%, 240px);
    // 宽屏避免显得“高”，小屏避免显得“矮”
    min-height: 50px;
    padding: clamp(10px, 1.05vw, 14px) clamp(14px, 1.4vw, 18px);
    border-radius: 10px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    text-decoration: none;
    color: inherit;
    transition:
      background-color 0.2s ease,
      transform 0.2s ease,
      border-color 0.2s ease;
    background-color: transparent;
    border: 2px solid rgba(22, 0, 4, 1);

    &:hover {
      transform: translateY(-1px);
      background-color: rgba(22, 0, 4, 1);
      border-color: rgba(22, 0, 4, 1);

      .resource-card__ctaLabel {
        color: rgba(255, 255, 255, 1);
      }
    }

    &:active {
      transform: translateY(0);
    }
  }

  .resource-card__ctaLabel {
    font-size: clamp(16px, 1.05vw, 20px);
    font-family: Red Rose-Medium;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
    color: rgba(22, 0, 4, 1);
  }

  // ---------------------------------------------------------------------------
  // About
  // ---------------------------------------------------------------------------
  .resources-about {
    width: 100%;
    max-width: var(--content-max);
    margin: clamp(64px, 10vw, 180px) auto 0;
    padding-inline: var(--container-pad);
    box-sizing: border-box;
    text-align: center;
  }

  .resources-about__title {
    margin: 0;
    color: rgba(22, 0, 4, 1);
    font-size: clamp(40px, 6vw, 80px);
    font-family: Red Rose-Medium;
    font-weight: normal;
    text-align: center;
    text-transform: uppercase;
    line-height: 1;
    white-space: normal;
  }

  .resources-about__text {
    margin: clamp(16px, 3.5vw, 40px) auto 0;
    max-width: min(92ch, 100%);
    color: rgba(124, 124, 124, 1);
    font-size: clamp(16px, 1.6vw, 24px);
    font-family: Red Rose-Regular;
    font-weight: normal;
    text-align: center;
    line-height: 1.7;
  }

  // ---------------------------------------------------------------------------
  // Breakpoints
  // ---------------------------------------------------------------------------
  @media (max-width: 1023px) {
    .resource-card {
      border-radius: 16px;
      flex-basis: calc((100% - var(--cards-gap)) / 2);
    }
  }

  @media (max-width: 767px) {
    .resources-hero {
      min-height: clamp(220px, 30vh, 340px);
    }

    .resource-cards {
      gap: 16px;
    }

    .resource-card {
      flex-basis: 100%;
    }

    .resource-card__cta {
      width: 100%;
    }
  }
}
